import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar, Clock, Plus, X, CalendarDays, Repeat } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface AvailabilitySlot {
  _id: string;
  day: string;
  timeSlots: string[];
  startDate?: string;
  endDate?: string;
  repeats?: 'daily' | 'weekly' | 'monthly';
  repeatInterval?: number;
  selectedDays?: string[];
  startTime?: string;
  endTime?: string;
  duration?: number;
}

interface OutlookAvailabilityPickerProps {
  availability: AvailabilitySlot[];
  onAvailabilityChange: (availability: AvailabilitySlot[]) => void;
  isEditing: boolean;
}

const DAYS_OF_WEEK = [
  { value: 'sunday', label: 'S', fullLabel: 'Sunday' },
  { value: 'monday', label: 'M', fullLabel: 'Monday' },
  { value: 'tuesday', label: 'T', fullLabel: 'Tuesday' },
  { value: 'wednesday', label: 'W', fullLabel: 'Wednesday' },
  { value: 'thursday', label: 'T', fullLabel: 'Thursday' },
  { value: 'friday', label: 'F', fullLabel: 'Friday' },
  { value: 'saturday', label: 'S', fullLabel: 'Saturday' }
];

const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const hour = Math.floor(i / 2);
  const minute = i % 2 === 0 ? '00' : '30';
  const time24 = `${hour.toString().padStart(2, '0')}:${minute}`;
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  const ampm = hour < 12 ? 'AM' : 'PM';
  return {
    value: time24,
    label: `${hour12}:${minute} ${ampm}`
  };
});

export const OutlookAvailabilityPicker: React.FC<OutlookAvailabilityPickerProps> = ({
  availability,
  onAvailabilityChange,
  isEditing
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSlot, setNewSlot] = useState<Partial<AvailabilitySlot>>({
    repeats: 'weekly',
    repeatInterval: 1,
    selectedDays: ['monday'],
    startTime: '09:00',
    endTime: '17:00',
    duration: 60,
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  });

  const handleAddSlot = () => {
    if (!newSlot.selectedDays?.length || !newSlot.startTime || !newSlot.endTime) {
      return;
    }

    const slot: AvailabilitySlot = {
      _id: Date.now().toString(),
      day: newSlot.selectedDays[0],
      timeSlots: [`${newSlot.startTime}-${newSlot.endTime}`],
      startDate: newSlot.startDate,
      endDate: newSlot.endDate,
      repeats: newSlot.repeats,
      repeatInterval: newSlot.repeatInterval,
      selectedDays: newSlot.selectedDays,
      startTime: newSlot.startTime,
      endTime: newSlot.endTime,
      duration: newSlot.duration
    };

    onAvailabilityChange([...availability, slot]);
    setShowAddForm(false);
    setNewSlot({
      repeats: 'weekly',
      repeatInterval: 1,
      selectedDays: ['monday'],
      startTime: '09:00',
      endTime: '17:00',
      duration: 60,
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    });
  };

  const handleRemoveSlot = (index: number) => {
    const updatedAvailability = availability.filter((_, i) => i !== index);
    onAvailabilityChange(updatedAvailability);
  };

  const handleDayToggle = (day: string) => {
    const selectedDays = newSlot.selectedDays || [];
    const updatedDays = selectedDays.includes(day)
      ? selectedDays.filter(d => d !== day)
      : [...selectedDays, day];
    
    setNewSlot({ ...newSlot, selectedDays: updatedDays });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (time: string) => {
    const [hour, minute] = time.split(':').map(Number);
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const ampm = hour < 12 ? 'AM' : 'PM';
    return `${hour12}:${minute.toString().padStart(2, '0')} ${ampm}`;
  };

  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-slate-900 flex items-center">
          <Clock className="w-5 h-5 mr-2 text-primary" />
          Availability Schedule
        </CardTitle>
        <CardDescription>Set your available time slots for mentoring sessions</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Existing Availability Slots */}
        {availability.map((slot, index) => (
          <Card key={slot._id} className="border-l-4 border-l-blue-500 bg-blue-50/30">
            <CardContent className="p-4">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-2">
                  <CalendarDays className="w-4 h-4 text-blue-600" />
                  <h4 className="font-medium text-slate-900">Time Slot #{index + 1}</h4>
                  {slot.repeats && (
                    <Badge variant="outline" className="bg-white">
                      <Repeat className="w-3 h-3 mr-1" />
                      {slot.repeats === 'weekly' ? 'Weekly' : slot.repeats === 'daily' ? 'Daily' : 'Monthly'}
                    </Badge>
                  )}
                </div>
                {isEditing && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveSlot(index)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 flex items-center">
                    <Clock className="w-3 h-3 mr-1" />
                    Time
                  </Label>
                  <div className="text-sm text-slate-900 font-medium">
                    {formatTime(slot.startTime || '09:00')} - {formatTime(slot.endTime || '17:00')}
                  </div>
                  <div className="text-xs text-slate-600">
                    Duration: {slot.duration || 60} minutes
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 flex items-center">
                    <CalendarDays className="w-3 h-3 mr-1" />
                    Days
                  </Label>
                  {slot.selectedDays && (
                    <div className="flex flex-wrap gap-1">
                      {slot.selectedDays.map(day => (
                        <Badge key={day} variant="secondary" className="text-xs bg-blue-100 text-blue-800">
                          {DAYS_OF_WEEK.find(d => d.value === day)?.fullLabel}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 flex items-center">
                    <Calendar className="w-3 h-3 mr-1" />
                    Period
                  </Label>
                  <div className="text-sm text-slate-900">
                    <div>{slot.startDate && formatDate(slot.startDate)}</div>
                    <div className="text-xs text-slate-600">to</div>
                    <div>{slot.endDate && formatDate(slot.endDate)}</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {/* Add New Slot Form */}
        {isEditing && (
          <>
            {showAddForm ? (
              <Card className="border-2 border-dashed border-blue-300 bg-blue-50/20">
                <CardContent className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h4 className="font-medium text-slate-900 flex items-center">
                      <Plus className="w-4 h-4 mr-2 text-blue-600" />
                      Add New Time Slot
                    </h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowAddForm(false)}
                      className="text-slate-500 hover:text-slate-700"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>

                  <div className="space-y-6">
                    {/* Time Selection */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700">Start Time</Label>
                        <Select
                          value={newSlot.startTime}
                          onValueChange={(value) => setNewSlot({ ...newSlot, startTime: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select start time" />
                          </SelectTrigger>
                          <SelectContent className="max-h-60">
                            {TIME_OPTIONS.map((time) => (
                              <SelectItem key={time.value} value={time.value}>
                                {time.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700">End Time</Label>
                        <Select
                          value={newSlot.endTime}
                          onValueChange={(value) => setNewSlot({ ...newSlot, endTime: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select end time" />
                          </SelectTrigger>
                          <SelectContent className="max-h-60">
                            {TIME_OPTIONS.map((time) => (
                              <SelectItem key={time.value} value={time.value}>
                                {time.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700">Session Duration (minutes)</Label>
                        <Select
                          value={newSlot.duration?.toString()}
                          onValueChange={(value) => setNewSlot({ ...newSlot, duration: parseInt(value) })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Duration" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="30">30 minutes</SelectItem>
                            <SelectItem value="45">45 minutes</SelectItem>
                            <SelectItem value="60">1 hour</SelectItem>
                            <SelectItem value="90">1.5 hours</SelectItem>
                            <SelectItem value="120">2 hours</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Days Selection */}
                    <div className="space-y-3">
                      <Label className="text-sm font-medium text-slate-700">Select Days</Label>
                      <div className="flex flex-wrap gap-2">
                        {DAYS_OF_WEEK.map((day) => (
                          <Button
                            key={day.value}
                            type="button"
                            variant={newSlot.selectedDays?.includes(day.value) ? "default" : "outline"}
                            size="sm"
                            onClick={() => handleDayToggle(day.value)}
                            className={`min-w-[3rem] ${
                              newSlot.selectedDays?.includes(day.value)
                                ? 'bg-blue-600 hover:bg-blue-700'
                                : 'hover:bg-blue-50'
                            }`}
                          >
                            {day.fullLabel}
                          </Button>
                        ))}
                      </div>
                    </div>

                    {/* Date Range */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700">Start Date</Label>
                        <Input
                          type="date"
                          value={newSlot.startDate}
                          onChange={(e) => setNewSlot({ ...newSlot, startDate: e.target.value })}
                          className="w-full"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700">End Date</Label>
                        <Input
                          type="date"
                          value={newSlot.endDate}
                          onChange={(e) => setNewSlot({ ...newSlot, endDate: e.target.value })}
                          className="w-full"
                        />
                      </div>
                    </div>

                    {/* Repeat Options */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700">Repeat</Label>
                        <Select
                          value={newSlot.repeats}
                          onValueChange={(value: 'daily' | 'weekly' | 'monthly') => 
                            setNewSlot({ ...newSlot, repeats: value })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="daily">Daily</SelectItem>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700">Every</Label>
                        <Select
                          value={newSlot.repeatInterval?.toString()}
                          onValueChange={(value) => setNewSlot({ ...newSlot, repeatInterval: parseInt(value) })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">1</SelectItem>
                            <SelectItem value="2">2</SelectItem>
                            <SelectItem value="3">3</SelectItem>
                            <SelectItem value="4">4</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowAddForm(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        onClick={handleAddSlot}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Time Slot
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddForm(true)}
                className="w-full border-2 border-dashed border-slate-300 hover:border-blue-400 hover:bg-blue-50 text-slate-600 hover:text-blue-600 py-8"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add New Time Slot
              </Button>
            )}
          </>
        )}

        {availability.length === 0 && !showAddForm && (
          <div className="text-center py-8 text-slate-500">
            <Clock className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-lg font-medium mb-2">No availability slots set</p>
            <p className="text-sm">Add your first time slot to start accepting mentoring sessions</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}; 